// Tools system (M3) — docs/ARCHITECTURE_M3.md §1, design plan §6.3.
// The buy-vs-build tension: an EDA tier dial (cost vs. a methodology multiplier
// snapshotted into projects), and per-discipline IN-HOUSE tool programs that
// accrue engineer-months into quality — cutting license spend and adding a small
// compounding productivity edge, at the cost of scarce heads, decay when
// unstaffed, and authored OBSOLESCENCE events when the industry methodology
// shifts under your niche tool (the historical trap, working as designed).

import type { Discipline } from "../types.js";
import { DISCIPLINES } from "../types.js";
import type { TickContext } from "../ctx.js";
import { emit } from "../ctx.js";

/** In-house tool quality from banked EM (0..1). */
export function toolQuality(em: number, tauEm: number): number {
  return 1 - Math.exp(-Math.max(0, em) / tauEm);
}

export function toolQualityOf(ctx: TickContext, d: Discipline): number {
  return toolQuality(ctx.s.tools.inhouse[d].em, ctx.content.economy.tools.inhouseTauEm);
}

/** Methodology multiplier for NEW projects (EDA tier, snapshotted at start). */
export function edaMethodMult(ctx: TickContext): number {
  return ctx.content.economy.tools.edaTiers[ctx.s.tools.edaTier].methodMult;
}

/** Per-discipline productivity bonus from a working in-house tool (people.ts). */
export function toolProdMult(ctx: TickContext, d: Discipline): number {
  return 1 + ctx.content.economy.tools.prodBonusMax * toolQualityOf(ctx, d);
}

/** Per-discipline seat-cost multiplier (opex): tier × in-house savings. */
export function toolCostMult(ctx: TickContext, d: Discipline): number {
  const td = ctx.content.economy.tools;
  const tier = td.edaTiers[ctx.s.tools.edaTier].costMult;
  return tier * (1 - td.licenseSavingsMax * toolQualityOf(ctx, d));
}

/** Runs after nodes, before projects: tool programs draw EM ahead of chips —
 *  the same scarce-heads bite as yield research. */
export function processTools(ctx: TickContext): void {
  const { s, content } = ctx;
  if (s.dead) return;
  const td = content.economy.tools;
  const m = s.monthIndex;

  for (const d of DISCIPLINES) {
    const tool = s.tools.inhouse[d];
    if (tool.staff > 0) {
      const draw = Math.min(tool.staff * ctx.monthsPerTick, ctx.emBudget[d]);
      ctx.emBudget[d] -= draw;
      ctx.allocation[d].tools += draw;
      const before = toolQuality(tool.em, td.inhouseTauEm);
      tool.em += draw;
      const after = toolQuality(tool.em, td.inhouseTauEm);
      if (!tool.announced && before < 0.5 && after >= 0.5) {
        tool.announced = true;
        emit(ctx, "toolShipped", `The in-house ${d} flow is real now — license renewals just got smaller.`, {
          discipline: d,
          quality: Math.round(after * 100) / 100,
        });
      }
    } else if (tool.em > 0) {
      // nobody maintains it: the flow rots against a moving industry
      tool.em *= Math.pow(td.decayMonthlyUnstaffed, ctx.monthsPerTick);
    }
  }

  // authored obsolescence: the methodology shifted; your niche tool didn't
  for (const ob of td.obsolescence) {
    if (ob.month !== m) continue;
    const tool = s.tools.inhouse[ob.discipline];
    if (tool.em > 1) {
      tool.em *= ob.factor;
      tool.announced = toolQuality(tool.em, td.inhouseTauEm) >= 0.5;
      emit(ctx, "toolObsoleted", `${ob.headline} — your in-house ${ob.discipline} flow is suddenly last-generation.`, {
        discipline: ob.discipline,
        headline: ob.headline,
        body: ob.body,
      });
    }
  }
}
